import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Sun, CheckCircle, Circle, ArrowRight, Clock, ListChecks, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const LS_KEY = "kaizen.today.completions";  // { "YYYY-MM-DD": { task_id: true } }

function loadCompletions() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
  catch { return {}; }
}
function saveCompletions(c) { localStorage.setItem(LS_KEY, JSON.stringify(c)); }

export default function TodayDashboardModule() {
  const [data, setData] = useState(null);
  const [completions, setCompletions] = useState(loadCompletions());
  const [loading, setLoading] = useState(true);
  const [pretestReady, setPretestReady] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    api.get("/today/tasks").then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(e => {
      toast.error("Impossible de charger les tâches du jour");
      setLoading(false);
    });
    api.get("/progression/pretest-ready-count").then(r => setPretestReady(r.data)).catch(() => {});
  }, []);

  function isDone(taskId) {
    return !!(completions[today] && completions[today][taskId]);
  }

  function toggle(taskId) {
    const next = { ...completions };
    if (!next[today]) next[today] = {};
    if (next[today][taskId]) {
      delete next[today][taskId];
      toast.success("Tâche décochée");
    } else {
      next[today][taskId] = new Date().toISOString();
      toast.success("✅ Tâche complétée");
    }
    setCompletions(next);
    saveCompletions(next);
  }

  if (loading) return <div className="p-6 text-zinc-400">Chargement…</div>;
  if (!data) return <div className="p-6 text-zinc-400">Aucune donnée</div>;

  const todayTasks = data.today_tasks || [];
  const upcoming = data.upcoming || [];
  const doneCount = todayTasks.filter(t => isDone(t.id)).length;
  const totalToday = todayTasks.length;
  const pendingMinutes = todayTasks.filter(t => !isDone(t.id)).reduce((s, t) => s + (t.estimated_minutes || 0), 0);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="today-dashboard">
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
        <div>
          <div className="flex items-center gap-2 text-amber-400">
            <Sun size={22} weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Aujourd'hui · {today}</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-100 mt-1">Tableau du jour</h1>
          <p className="text-sm text-zinc-500 mt-1">3 priorités · cocher au fur et à mesure · stocké localement (zéro friction)</p>
        </div>
        <Link
          to="/app/morning-brief"
          data-testid="link-morning-brief"
          className="px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs uppercase tracking-widest font-bold hover:bg-amber-500 hover:text-zinc-950 transition flex items-center gap-2"
        >
          <Sparkle size={14} /> Morning Brief
        </Link>
      </header>

      {pretestReady && pretestReady.count > 0 && (
        <Link to="/app/carnet-progression" data-testid="pretest-ready-badge"
          className="flex items-center justify-between gap-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/40 hover:bg-emerald-500/20 transition group">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🥋</span>
            <div>
              <div className="text-emerald-200 font-bold">{pretestReady.count} élève(s) prêt(s) pour le prétest</div>
              <div className="text-xs text-emerald-300/70">Au-dessus du seuil — planifie leur convocation et leur diplôme de ceinture</div>
            </div>
          </div>
          <span className="text-emerald-300 text-xs uppercase tracking-widest font-bold flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
            Ouvrir les carnets <ArrowRight size={14} />
          </span>
        </Link>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Stat icon={ListChecks} label="Tâches aujourd'hui" value={`${doneCount}/${totalToday}`} accent="text-amber-400" testid="stat-today" />
        <Stat icon={CheckCircle} label="Complétées" value={doneCount} accent="text-emerald-400" testid="stat-done" />
        <Stat icon={Clock} label="Minutes restantes" value={`${pendingMinutes} min`} accent="text-sky-400" testid="stat-minutes" />
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-widest">🔥 À faire aujourd'hui</h2>
        {todayTasks.length === 0 && (
          <div className="text-sm text-zinc-500 italic p-6 border border-dashed border-zinc-800 rounded">Aucune tâche prévue.</div>
        )}
        {todayTasks.map(t => (
          <TaskCard key={t.id} task={t} done={isDone(t.id)} onToggle={() => toggle(t.id)} />
        ))}
      </section>

      {upcoming.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold text-zinc-400 uppercase tracking-widest">📅 À venir cette semaine</h2>
          <div className="grid gap-2">
            {upcoming.map(t => (
              <div key={t.id} data-testid={`upcoming-${t.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800/60">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-300 truncate">{t.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{t.subtitle}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-amber-500/70 whitespace-nowrap">J+{t.day_offset}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="p-4 rounded-lg bg-zinc-900/60 border border-zinc-800">
      <Icon size={18} className={accent} />
      <div className="mt-2 text-2xl font-bold text-zinc-100">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function TaskCard({ task, done, onToggle }) {
  return (
    <div
      data-testid={`task-${task.id}`}
      className={`group flex items-start gap-3 p-4 rounded-lg border transition ${
        done ? "bg-emerald-950/30 border-emerald-800/50 opacity-70" : "bg-zinc-900/70 border-zinc-800 hover:border-amber-700/50"
      }`}
    >
      <button
        onClick={onToggle}
        data-testid={`toggle-${task.id}`}
        className={`mt-0.5 transition ${done ? "text-emerald-400" : "text-zinc-500 hover:text-amber-400"}`}
        aria-label={done ? "Marquer non fait" : "Marquer fait"}
      >
        {done ? <CheckCircle size={26} weight="fill" /> : <Circle size={26} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-base font-bold ${done ? "line-through text-zinc-500" : "text-zinc-100"}`}>{task.title}</div>
        <div className="text-sm text-zinc-400 mt-0.5">{task.subtitle}</div>
        <div className="flex items-center gap-3 mt-2 text-xs">
          <span className="text-amber-500/80">⏱ {task.estimated_minutes} min</span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500 uppercase tracking-wider">{task.category}</span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500">{task.priority}</span>
        </div>
      </div>
      {task.action_url && (
        task.action_url.startsWith("http") ? (
          <a
            href={task.action_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`action-${task.id}`}
            className="shrink-0 px-3 py-2 rounded-lg bg-amber-500 text-zinc-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition flex items-center gap-1.5"
          >
            {task.action_label || "Ouvrir"} <ArrowRight size={12} weight="bold" />
          </a>
        ) : (
          <Link
            to={task.action_url.replace(/^\/app\//, "/app/")}
            data-testid={`action-${task.id}`}
            className="shrink-0 px-3 py-2 rounded-lg bg-amber-500 text-zinc-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition flex items-center gap-1.5"
          >
            {task.action_label || "Ouvrir"} <ArrowRight size={12} weight="bold" />
          </Link>
        )
      )}
    </div>
  );
}
